import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { TICKET_TYPES } from "@circle-react/components/EventsV3/Modal/utils";
import { ViewType } from "@circle-react/components/LiveStreams/LiveFormV3/Modal/Common/ViewType";
import { dateIsPast } from "@circle-react/helpers/dateTimeHelpers";
import { ROOM_ACCESS_TYPES } from "@circle-react/helpers/liveStreamRoomHelpers";
import { ROOM_STATUS } from "@circle-react/helpers/liveStreamRoomHelpers";
import type { RoomType } from "@circle-react/helpers/liveStreamRoomHelpers";
import { Form } from "@circle-react-uikit/Form";
import { FormItem } from "../../../../Common";
import { AccessType as AccessTypeEvents } from "../AccessType";

interface LiveStreamLocationProps {
  event?: any;
  recordingEnabled: boolean;
  roomTypeOverride?: RoomType;
  showRecordingToggle?: boolean;
}
export const LiveStreamLocation = ({
  recordingEnabled,
  showRecordingToggle = false,
  roomTypeOverride,
  event,
}: LiveStreamLocationProps) => {
  const isEditMode = !!event;

  const isRecordingToggleDisabled = () => {
    const { event_setting_attributes: eventData } = event || {};
    const isEventEnded = eventData ? dateIsPast(eventData?.ends_at) : false;
    const streamStatusIfAvailable =
      eventData?.live_stream_room_setting_attributes?.stream_status;
    return isEventEnded || streamStatusIfAvailable === ROOM_STATUS.LIVE;
  };

  const { watch, setValue } = useFormContext();
  const accessType = watch(
    "event_setting_attributes.live_stream_room_setting_attributes.access_type",
  );
  const autoPostRecordingEnabled = watch(
    "event_setting_attributes.live_stream_room_setting_attributes.auto_post_recording_enabled",
  );

  const ticketType = watch("event_setting_attributes.ticket_type");

  const shouldHideLimitUrlSharingToggle =
    accessType === ROOM_ACCESS_TYPES.PUBLIC_STREAM;

  const limitUrlSharingName =
    "event_setting_attributes.live_stream_room_setting_attributes.limit_url_sharing";

  const prevTicketTypeRef = useRef(
    event?.event_setting_attributes?.ticket_type,
  );

  const defaultLimitUrlSharingNameValue =
    isEditMode && prevTicketTypeRef.current == ticketType
      ? event?.event_setting_attributes?.live_stream_room_setting_attributes
          ?.limit_url_sharing
      : ticketType === TICKET_TYPES.PAID;

  useEffect(() => {
    if (accessType !== ROOM_ACCESS_TYPES.PUBLIC_STREAM) return;
    setValue(limitUrlSharingName, false, { shouldDirty: true });
  }, [accessType, setValue]);

  useEffect(() => {
    setValue(limitUrlSharingName, defaultLimitUrlSharingNameValue, {
      shouldDirty: true,
    });
  }, [ticketType, setValue]);

  return (
    <>
      <div className="flex items-stretch">
        <div className="w-1/2 pr-2">
          <FormItem
            name="event_setting_attributes.live_stream_room_setting_attributes.view_type"
            labelClassName="mb-3 text-lg"
            translationRoot="events.create.form"
          >
            <ViewType roomType={roomTypeOverride} />
          </FormItem>
        </div>
        <div className="w-1/2 pl-2">
          <FormItem
            name="event_setting_attributes.live_stream_room_setting_attributes.access_type"
            labelClassName="mb-3 text-lg"
            translationRoot="events.create.form"
          >
            <AccessTypeEvents
              roomType={roomTypeOverride}
              name="event_setting_attributes.live_stream_room_setting_attributes.access_type"
            />
          </FormItem>
        </div>
      </div>
      {showRecordingToggle && (
        <div className="mt-4">
          <FormItem
            name="event_setting_attributes.live_stream_room_setting_attributes.recording_enabled"
            tooltipText={t(
              "events.create_modal.event_setting_attributes.recording_enabled_tooltip",
            )}
            inlineReverse
            translationRoot="events.create.form"
            className="!pt-1"
            labelClassName="!font-normal"
            isDisabled={isRecordingToggleDisabled()}
          >
            <Form.ToggleSwitch
              variant="small"
              disabled={isRecordingToggleDisabled()}
              name="event_setting_attributes.live_stream_room_setting_attributes.recording_enabled"
            />
          </FormItem>
          {recordingEnabled && (
            <FormItem
              name="event_setting_attributes.live_stream_room_setting_attributes.auto_post_recording_enabled"
              tooltipText={t(
                "events.create_modal.event_setting_attributes.auto_post_recording_enabled_tooltip",
              )}
              inlineReverse
              translationRoot="events.create.form"
              className="!pt-1"
              labelClassName="!font-normal"
            >
              <Form.ToggleSwitch
                variant="small"
                name="event_setting_attributes.live_stream_room_setting_attributes.auto_post_recording_enabled"
              />
            </FormItem>
          )}
          <FormItem
            name="event_setting_attributes.live_stream_room_setting_attributes.mute_on_join"
            inlineReverse
            translationRoot="events.create.form"
            className="!pt-1"
            labelClassName="!font-normal"
          >
            <Form.ToggleSwitch
              variant="small"
              name="event_setting_attributes.live_stream_room_setting_attributes.mute_on_join"
            />
          </FormItem>
          <FormItem
            name="event_setting_attributes.live_stream_room_setting_attributes.hide_participants_list"
            tooltipText={t(
              `events.create.form.event_setting_attributes.live_stream_room_setting_attributes.hide_participants_list_tooltip`,
            )}
            inlineReverse
            translationRoot="events.create.form"
            className="!pt-1"
            labelClassName="!font-normal"
          >
            <Form.ToggleSwitch
              variant="small"
              name="event_setting_attributes.live_stream_room_setting_attributes.hide_participants_list"
            />
          </FormItem>
        </div>
      )}
      <FormItem
        name="event_setting_attributes.hide_location_from_non_attendees"
        inlineReverse
        translationRoot={
          autoPostRecordingEnabled
            ? "events.create.form.live_location"
            : "events.create.form"
        }
        className="!pt-1"
        labelClassName="!font-normal"
        tooltipText={
          autoPostRecordingEnabled &&
          t(
            "events.create_modal.event_setting_attributes.hide_location_from_non_attendees_tooltip",
          )
        }
      >
        <Form.ToggleSwitch
          variant="small"
          name="event_setting_attributes.hide_location_from_non_attendees"
        />
      </FormItem>
      {!shouldHideLimitUrlSharingToggle && (
        <FormItem
          name={limitUrlSharingName}
          translationRoot="events.create.form"
          tooltipText={t(
            `events.create_modal.event_setting_attributes.limit_url_sharing_tooltip`,
          )}
          inlineReverse
          className="!pt-1"
          labelClassName="!font-normal"
        >
          <Form.ToggleSwitch variant="small" name={limitUrlSharingName} />
        </FormItem>
      )}
    </>
  );
};
